import React from 'react';
import {
  Container,
  Typography,
  Box,
  Paper,
  Breadcrumbs,
  Link,
  CircularProgress,
  Grid,
  Chip,
  IconButton,
  Tooltip,
  Alert,
  Button,
} from '@mui/material';
import {
  DataGrid,
  GridColDef,
  GridRenderCellParams,
} from '@mui/x-data-grid';
import { Home, Schedule, Refresh, ArrowBack, PauseCircleOutline, PlayCircleOutline, PlayArrow } from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuthenticatedFetch } from '../../hooks/useAuthenticatedFetch';
import { API_URL } from '../../utils/config';

interface ScheduledTaskRun {
  id: number;
  status: 'running' | 'completed' | 'failed' | 'skipped';
  startedAt: string;
  completedAt: string | null;
  durationMs: number | null;
  errorMessage: string | null;
  metadata: string | null;
  triggerType?: string;
}

interface ScheduledTaskDetail {
  functionName: string;
  appName: string;
  description: string;
  schedule: string;
  scheduleDescription: string;
  nextRun: string;
  isEnabled: boolean;
}

interface ScheduledTaskRunsResponse {
  task: ScheduledTaskDetail;
  runs: ScheduledTaskRun[];
  pagination: {
    page: number;
    pageSize: number;
    total: number;
    totalPages: number;
  };
}

interface RunEventRow {
  id: string;
  runId: number;
  eventType: 'start' | 'end';
  timestamp: string;
  status: 'running' | 'completed' | 'failed' | 'skipped';
  description: string;
  durationMs: number | null;
  errorMessage: string | null;
}

const formatDuration = (ms: number | null): string => {
  if (ms === null) return '-';
  if (ms < 1000) return `${ms}ms`;
  if (ms < 60000) return `${(ms / 1000).toFixed(1)}s`;
  return `${(ms / 60000).toFixed(1)}m`;
};

const getStatusColor = (status: string): 'success' | 'error' | 'info' | 'warning' | 'default' => {
  switch (status) {
    case 'completed': return 'success';
    case 'failed': return 'error';
    case 'running': return 'info';
    case 'skipped': return 'warning';
    default: return 'default';
  }
};

export const ScheduledTaskDetailPage: React.FC = () => {
  const { functionName } = useParams<{ functionName: string }>();
  const { authenticatedFetch } = useAuthenticatedFetch();
  const navigate = useNavigate();

  const { data, isLoading, error, refetch, isFetching } = useQuery<ScheduledTaskRunsResponse>({
    queryKey: ['scheduled-task-runs', functionName],
    queryFn: async () => {
      const response = await authenticatedFetch(
        `${API_URL}/api/developer-tools/scheduled-tasks/${functionName}/runs?pageSize=100`
      );
      if (!response || !response.ok) throw new Error('Failed to fetch task runs');
      const result = await response.json();
      return result.data;
    },
    enabled: !!functionName,
  });

  const queryClient = useQueryClient();

  const toggleEnabled = useMutation({
    mutationFn: async (enabled: boolean) => {
      const response = await authenticatedFetch(
        `${API_URL}/api/developer-tools/scheduled-tasks/${functionName}`,
        {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ enabled }),
        }
      );
      if (!response || !response.ok) throw new Error('Failed to update task');
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['scheduled-task-runs', functionName] });
      queryClient.invalidateQueries({ queryKey: ['scheduled-tasks'] });
    },
  });

  const triggerTask = useMutation({
    mutationFn: async () => {
      const response = await authenticatedFetch(
        `${API_URL}/api/developer-tools/scheduled-tasks/${functionName}/trigger`,
        { method: 'POST' }
      );
      if (!response || !response.ok) throw new Error('Failed to trigger task');
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['scheduled-task-runs', functionName] });
      queryClient.invalidateQueries({ queryKey: ['scheduled-tasks'] });
    },
  });

  // Transform runs into two-row event display
  const eventRows: RunEventRow[] = [];
  for (const run of (data?.runs || [])) {
    if (run.status === 'skipped') {
      // Skipped runs are shown as a single row (no start/end pair)
      eventRows.push({
        id: `${run.id}-skip`,
        runId: run.id,
        eventType: 'end',
        timestamp: run.startedAt,
        status: 'skipped',
        description: run.metadata || 'Skipped: task is paused',
        durationMs: null,
        errorMessage: null,
      });
    } else {
      // existing start row
      eventRows.push({
        id: `${run.id}-start`,
        runId: run.id,
        eventType: 'start',
        timestamp: run.startedAt,
        status: run.status,
        description: run.triggerType === 'manual' ? 'Manual Trigger Started' : 'Scheduled Task Started',
        durationMs: null,
        errorMessage: null,
      });

      if (run.status !== 'running') {
        let endDescription: string;
        if (run.status === 'failed') {
          endDescription = `Scheduled Task Failed${run.errorMessage ? `: ${run.errorMessage}` : ''}`;
        } else {
          endDescription = run.metadata
            ? `Scheduled Task Completed — ${run.metadata}`
            : 'Scheduled Task Completed';
        }
        eventRows.push({
          id: `${run.id}-end`,
          runId: run.id,
          eventType: 'end',
          timestamp: run.completedAt ?? run.startedAt,
          status: run.status,
          description: endDescription,
          durationMs: run.durationMs,
          errorMessage: run.errorMessage,
        });
      }
    }
  }

  const task = data?.task;

  const columns: GridColDef[] = [
    {
      field: 'timestamp',
      headerName: 'Timestamp',
      width: 200,
      type: 'dateTime',
      valueGetter: (_value: unknown, row: RunEventRow) => new Date(row.timestamp),
      valueFormatter: (value: Date) => (value && !isNaN(value.getTime())) ? value.toLocaleString() : '',
    },
    {
      field: 'description',
      headerName: 'Event',
      flex: 1,
    },
    {
      field: 'status',
      headerName: 'Status',
      width: 120,
      renderCell: (params: GridRenderCellParams) => {
        const row = params.row as RunEventRow;
        return (
          <Chip
            label={row.status.charAt(0).toUpperCase() + row.status.slice(1)}
            size="small"
            color={getStatusColor(row.status)}
          />
        );
      },
    },
    {
      field: 'durationMs',
      headerName: 'Duration',
      width: 120,
      valueFormatter: (value: number | null) => formatDuration(value),
    },
  ];

  const renderTaskInfoField = (label: string, value: string | null | undefined, monospace = false) => (
    <Box sx={{ mb: 1 }}>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.25 }}>
        {label}
      </Typography>
      <Typography
        variant="body1"
        sx={{
          fontFamily: monospace ? 'monospace' : 'inherit',
          bgcolor: monospace ? 'grey.100' : 'transparent',
          px: monospace ? 1 : 0,
          py: monospace ? 0.5 : 0,
          borderRadius: 1,
          display: 'inline-block',
        }}
      >
        {value || '—'}
      </Typography>
    </Box>
  );

  return (
    <Container maxWidth="xl">
      <Box sx={{ py: 4 }}>
        {/* Breadcrumbs */}
        <Breadcrumbs sx={{ mb: 3 }}>
          <Link
            color="inherit"
            href="/"
            onClick={(e) => {
              e.preventDefault();
              navigate('/');
            }}
            sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}
          >
            <Home sx={{ mr: 0.5 }} fontSize="small" />
            Home
          </Link>
          <Link
            color="inherit"
            href="/admin/hub"
            onClick={(e) => {
              e.preventDefault();
              navigate('/admin/hub');
            }}
            sx={{ cursor: 'pointer' }}
          >
            Admin Hub
          </Link>
          <Link
            color="inherit"
            href="/admin/hub/system/scheduled-tasks"
            onClick={(e) => {
              e.preventDefault();
              navigate('/admin/hub/system/scheduled-tasks');
            }}
            sx={{ cursor: 'pointer' }}
          >
            Scheduled Tasks
          </Link>
          <Typography color="text.primary">{functionName}</Typography>
        </Breadcrumbs>

        {/* Header */}
        <Box sx={{ mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Tooltip title="Back to Scheduled Tasks">
                <IconButton
                  onClick={() => navigate('/admin/hub/system/scheduled-tasks')}
                  sx={{ color: 'text.secondary' }}
                >
                  <ArrowBack />
                </IconButton>
              </Tooltip>
              <Schedule sx={{ fontSize: 40, color: 'primary.main' }} />
              <Box>
                <Typography variant="h4" component="h1">
                  {functionName}
                </Typography>
                {task?.description && (
                  <Typography variant="body1" color="text.secondary">
                    {task.description}
                  </Typography>
                )}
              </Box>
            </Box>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                variant={task?.isEnabled ? 'outlined' : 'contained'}
                color="warning"
                startIcon={task?.isEnabled ? <PauseCircleOutline /> : <PlayCircleOutline />}
                onClick={() => toggleEnabled.mutate(!task?.isEnabled)}
                disabled={!task || isLoading || toggleEnabled.isPending}
              >
                {task?.isEnabled ? 'Pause' : 'Resume'}
              </Button>
              <Button
                variant="outlined"
                color="primary"
                startIcon={<PlayArrow />}
                onClick={() => triggerTask.mutate()}
                disabled={!task || isLoading}
              >
                Run Now
              </Button>
              <Button
                variant="outlined"
                startIcon={isFetching ? <CircularProgress size={20} /> : <Refresh />}
                onClick={() => refetch()}
                disabled={isFetching}
              >
                {isFetching ? 'Refreshing...' : 'Refresh'}
              </Button>
            </Box>
          </Box>
        </Box>

        {/* Error State */}
        {error && (
          <Alert severity="error" sx={{ mb: 3 }} action={
            <Button color="inherit" size="small" onClick={() => refetch()}>Retry</Button>
          }>
            Failed to load task runs: {(error as Error).message}
          </Alert>
        )}

        {task && !task.isEnabled && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            This task is currently paused. Scheduled runs will be skipped until resumed.
          </Alert>
        )}

        {toggleEnabled.isError && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => toggleEnabled.reset()}>
            Failed to {task?.isEnabled ? 'pause' : 'resume'} task: {(toggleEnabled.error as Error).message}
          </Alert>
        )}
        {triggerTask.isError && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => triggerTask.reset()}>
            Failed to trigger task: {(triggerTask.error as Error).message}
          </Alert>
        )}

        {/* Task Info Card */}
        {task && (
          <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
            <Grid container spacing={3}>
              <Grid item xs={12} sm={6} md={3}>
                {renderTaskInfoField('Function Name', task.functionName, true)}
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                {renderTaskInfoField('App Name', task.appName)}
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                {renderTaskInfoField('Schedule', task.schedule, true)}
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                {renderTaskInfoField('Next Run', task.isEnabled ? (task.nextRun ? new Date(task.nextRun).toLocaleString() : null) : 'Paused')}
              </Grid>
              <Grid item xs={12}>
                {renderTaskInfoField('Schedule Description', task.scheduleDescription)}
              </Grid>
            </Grid>
          </Paper>
        )}

        {/* Empty State */}
        {data?.runs?.length === 0 && !isLoading && (
          <Alert severity="info" sx={{ mb: 3 }}>
            No runs recorded yet. This task will appear here after its next scheduled execution.
          </Alert>
        )}

        {/* Run History DataGrid */}
        <Box sx={{ width: '100%' }}>
          <Typography variant="h6" sx={{ mb: 2 }}>
            Run History
          </Typography>
          <DataGrid
            rows={eventRows}
            columns={columns}
            getRowId={(row) => row.id}
            loading={isLoading}
            autoHeight
            disableRowSelectionOnClick
            getRowClassName={(params) => {
              const row = params.row as RunEventRow;
              return row.runId % 2 === 0 ? 'even-run' : 'odd-run';
            }}
            initialState={{
              sorting: {
                sortModel: [{ field: 'timestamp', sort: 'desc' }],
              },
            }}
            sx={{
              '& .even-run': { bgcolor: 'action.hover' },
              '& .odd-run': { bgcolor: 'background.paper' },
            }}
          />
        </Box>
      </Box>
    </Container>
  );
};
